<script setup xmlns="http://www.w3.org/1999/html">
import { RouterLink, RouterView, useRoute } from "vue-router";
import Preview from "./components/Templates/Preview.vue";
import {
  getCurrentInstance,
  onMounted,
  ref,
  watch,
  computed,
  onBeforeUnmount,
} from "vue";
import Options from "./components/Templates/Options.vue";
import ImagePreview from "./components/ImagePreview.vue";
import { useI18n } from "./i18n";

const option = ref({});
const route = useRoute();
const img = ref();
const productType = ref();
const test = ref(false);
const input = ref(null);
const instance = ref(getCurrentInstance());
const { t, setLocale, availableLocales, locale } = useI18n();
const showLanguageMenu = ref(false);
const isMobile = ref(false);
const languages = [
  { code: "de", label: "🇩🇪 Deutsch" },
  { code: "en", label: "🇺🇸 English" },
  { code: "fr", label: "🇫🇷 Français" },
];
const currentLocale = computed(() => locale?.value || locale);

if (performance.navigation.type === 1) {
  setTimeout(() => {
    if (route.query.v == "8341420540169") {
      location.href = "/pages/dein-eigenes-foto?v=8341420540169";
    } else {
      location.href = "/pages/dein-eigenes-foto";
    }
  }, "300");
}
onMounted(() => {
  updateIsMobile();
  window.addEventListener("resize", updateIsMobile);
  syncLocaleFromRoute(route.query.lang);
  const fileInputEl = input.value;
  fileInputEl.dispatchEvent(new MouseEvent("click", { bubbles: true }));
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateIsMobile);
});

watch(
  () => route.query.lang,
  (newLang) => {
    syncLocaleFromRoute(newLang);
  },
  { immediate: false }
);

function handleFileChange(event) {
  img.value = URL.createObjectURL(event.target.files[0]);
}

function result(resultImg) {
  img.value = resultImg;
}

function syncLocaleFromRoute(langParam) {
  const normalized =
    typeof langParam === "string" ? langParam.toLowerCase() : "";
  if (availableLocales.includes(normalized)) {
    setLocale(normalized);
  } else if (!langParam) {
    setLocale("de");
  }
}

function updateIsMobile() {
  isMobile.value = window.innerWidth < 820;
  if (!isMobile.value) {
    showLanguageMenu.value = false;
  }
}

function toggleLanguageMenu() {
  showLanguageMenu.value = !showLanguageMenu.value;
}

function selectLanguage(code) {
  setLocale(code);
  showLanguageMenu.value = false;
}
</script>

<template>
  <section>
    <div class="language-switcher" v-if="!isMobile">
      <button
        v-for="lang in languages"
        :key="lang.code"
        type="button"
        :class="{ active: currentLocale.value === lang.code }"
        @click="setLocale(lang.code)"
      >
        {{ lang.label }}
      </button>
    </div>
    <section
      v-if="img"
      class="main"
      :class="{
        two_col: $route.path !== '/vorschau',
        vorschau: $route.path === '/vorschau',
      }"
    >
      <main
        class="mainWrapper"
        :class="{ wandbildMain: $route.matched[0]?.props.default.wand }"
        v-if="$route.path !== '/vorschau'"
      >
        <Preview
          @result="result"
          :option="option"
          :srcbild="img"
          :type="$route.query.v"
        />
      </main>
      <main v-else>
        <image-preview
          :option="option"
          :srcbild="img"
          :type="$route.query.v"
        ></image-preview>
      </main>
      <RouterView :srcbild="img" :option="option" :productType="productType" />
      <a
        class="back"
        v-if="$route.path !== '/vorschau'"
        onclick="location.reload()"
        >{{ t("back") }}</a
      >
    </section>
    <section v-else class="main upload">
      <div class="upload">
        <div v-if="isMobile" class="home-language-selector">
          <div class="home-lang-menu">
            <button
              v-for="lang in languages"
              :key="`home-${lang.code}`"
              type="button"
              :class="{ active: currentLocale.value === lang.code }"
              @click="selectLanguage(lang.code)"
            >
              {{ lang.label }}
            </button>
          </div>
        </div>
        <h2>{{ t("uploadTipsTitle") }}</h2>
        <div class="info">
          <p>
            <img
              src="https://picmondo.de/projekte/generator/dist/assets/img/qualitaet.svg"
            />
            <b>{{ t("uploadQualityTitle") }}</b>
            {{ t("uploadQualityBody") }}
          </p>
          <p>
            <img
              src="https://picmondo.de/projekte/generator/dist/assets/img/belichtung.svg"
            />
            <b>{{ t("uploadLightingTitle") }}</b>
            {{ t("uploadLightingBody") }}
          </p>
          <p>
            <img
              src="https://picmondo.de/projekte/generator/dist/assets/img/auswahl.svg"
            />
            <b>{{ t("uploadSizeTitle") }}</b>
            {{ t("uploadSizeBody") }}
          </p>
        </div>
        <div class="uploadButtons">
          <label for="file-upload" class="custom-file-upload">
            {{ t("uploadPhoto") }}
          </label>
          <input
            id="file-upload"
            class="custom-file-input"
            type="file"
            ref="input"
            @change="handleFileChange"
          />
          <a href="https://picmondoo.de/" class="backtoStore"
            ><p class="inner">{{ t("backToShop") }}</p></a
          >
        </div>
      </div>
    </section>
  </section>
  <!--  <image-preview v-else :option="option" :srcbild="img" :type="$route.query.v"></image-preview>-->
</template>

<style scoped>
.upload {
  display: grid;
  grid-template-rows: 1fr 1fr 1fr;
  justify-content: center;
  align-items: center;
  background: #fff;
  max-height: 100vh;
  position: relative;
}
.uploadButtons {
  display: flex;
  max-width: 100%;
}
.info {
  display: flex;
  gap: 100px;
  color: #001761;
}

.info p {
  max-width: 450px;
  font-size: 18px;
  text-align: justify;
}
.info img {
  display: block;
  margin: 0 auto;
}
.info b {
  display: block;
  font-weight: 700;
  text-align: center;
  margin: 15px 0;
}
#app {
  grid-template-columns: 1fr;
}
.custom-file-input::-webkit-file-upload-button {
  visibility: hidden;
}
.custom-file-upload {
  text-align: center;
  width: 400px;
  margin: 0 auto;
  height: 70px;
  max-width: 100%;
}
.custom-file-input {
  display: none;
}
.custom-file-upload,
.back {
  display: inline-block;
  border-radius: 15px;
  outline: none;
  white-space: nowrap;
  -webkit-user-select: none;
  user-select: none;
  cursor: pointer;
  text-align: center;
  background: linear-gradient(
    147deg,
    rgba(255, 125, 176, 1) 20%,
    rgba(255, 221, 90, 1) 100%
  );
  color: #fff;
  font-weight: 700;
  font-size: 24px;
  margin: 0 auto;
  padding: 20px;
  border: none;
  font-size: 24px !important;
  line-height: 29px;
}
.back {
  position: absolute;
  left: 40px;
  bottom: 4.1%;
  width: 160px;
  font-size: 18pt;
  background: #fff;
  color: #001761;
  padding: 3pt 10pt;
  border: 2px solid #001761;
}
.custom-file-upload:hover {
  border-color: black;
}
h2 {
  text-align: center;
  color: #001761;
  font-size: 24px;
  font-weight: 700;
}
@media (min-width: 1080px) {
  .uploadButtons {
    justify-content: center;
    gap: 50px;
  }
  .backtoStore,
  .custom-file-upload {
    margin: unset;
  }
  .custom-file-upload {
    order: 1;
  }
  .backtoStore .inner {
    height: 65px;
    font-size: 24px;
  }
  .backtoStore {
    height: 69px;
  }
}

@media (max-width: 720px) {
  .uploadButtons {
    flex-direction: column;
  }
  .info,
  .upload {
    display: block;
  }
  .upload h2 {
    margin: 0;
    font-size: 22px;
  }
  .info img {
    height: 25px;
  }
  .info {
    margin-bottom: 20px;
  }
  .info p {
    font-size: 12px;
    line-height: 16px;
    margin: 15px auto 0px;
  }
  .custom-file-upload {
    margin-top: 20px;
    font-size: 17px !important;
    height: 60px;
    margin-bottom: 10px;
    line-height: 20px;
  }
  h2 {
    margin: 50px 0;
  }
  p {
    margin-bottom: 50px;
  }
}
@media (max-width: 560px) {
  section.main {
    padding: 0;
  }
  .upload {
    width: 100%;
    padding: 0 20px;
    display: flex;
    height: 100svh;
    flex-direction: column;
  }
  .upload input {
    color: transparent;
  }
  .custom-file-upload::before {
    font-size: 10.8pt;
  }
  a.back {
    display: none;
  }
}
.language-switcher {
  position: absolute;
  bottom: 0;
  right: 1rem;
  display: flex;
  gap: 8px;
  z-index: 10;
  background: linear-gradient(
    147deg,
    rgba(255, 125, 176, 1) 20%,
    rgba(255, 221, 90, 1) 100%
  );
  padding: 10px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}
.language-switcher button {
  background: #001761;
  color: white;
  font-weight: 700;
  border-radius: 10px;
  padding: 6px 12px;
  cursor: pointer;
  border: none;
}
.language-switcher button.active {
  background: linear-gradient(
    147deg,
    rgba(255, 125, 176, 1) 20%,
    rgba(255, 221, 90, 1) 100%
  );
  color: #fff;
  border-color: transparent;
}
.language-switcher-mobile {
  position: fixed;
  top: 50px;
  right: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}
.language-switcher-mobile .lang-toggle {
  border: none;
  background: linear-gradient(
    147deg,
    rgba(255, 125, 176, 1) 20%,
    rgba(255, 221, 90, 1) 100%
  );
  color: #fff;
  font-weight: 700;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
}
.language-switcher-mobile .lang-menu {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #001761;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}
.language-switcher-mobile .lang-menu button {
  border: none;
  background: transparent;
  color: #001761;
  font-weight: 700;
  padding: 10px 16px;
  text-align: left;
  cursor: pointer;
}
.language-switcher-mobile .lang-menu button + button {
  border-top: 1px solid #e5e7eb;
}
.language-switcher-mobile .lang-menu button.active {
  background: linear-gradient(
    147deg,
    rgba(255, 125, 176, 1) 20%,
    rgba(255, 221, 90, 1) 100%
  );
  color: #fff;
}
.home-language-selector {
  width: 100%;
}
.home-language-selector .home-lang-toggle {
  border: none;
  background: linear-gradient(
    147deg,
    rgba(255, 125, 176, 1) 20%,
    rgba(255, 221, 90, 1) 100%
  );
  color: #fff;
  font-weight: 700;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  padding: 10px 16px;
  cursor: pointer;
}
.home-language-selector .home-lang-menu {
  display: flex;
  flex-direction: row;
  padding-bottom: 30px;
  margin-bottom: 30px;
  border-bottom: 1px solid #e7e7e7;
  width: 100%;
  align-items: center;
  justify-content: center;
}
.home-language-selector .home-lang-menu button {
  border: none;
  background: transparent;
  color: #001761;
  font-weight: 700;
  padding: 10px 16px;
  text-align: left;
  cursor: pointer;
}
.home-language-selector .home-lang-menu button.active {
  background: linear-gradient(
    147deg,
    rgba(255, 125, 176, 1) 20%,
    rgba(255, 221, 90, 1) 100%
  );
  color: #fff;
}
@media (max-width: 819px) {
  .language-switcher {
    display: none;
  }
}
</style>
